<template>
  <div class="premium-toolbar-container">
    <div class="toolbar-main">
      <div class="toolbar-content">
        <!-- History Group -->
        <ToolbarGroup>
          <ToolbarButton
            @click="executeCommand('undo')"
            :disabled="!canUndo"
            title="Undo (Ctrl+Z)"
          >
            <Previous/>
          </ToolbarButton>
          <ToolbarButton
            @click="executeCommand('redo')"
            :disabled="!canRedo"
            title="Redo (Ctrl+Y)"
          >
            <Next/>
          </ToolbarButton>
        </ToolbarGroup>
        <!-- Headings Group -->
        <ToolbarGroup>
          <HeadingDropdown
            :model-value="currentHeading"
            @change="setHeading"
            class="margin-right-0"
          />
          <!-- Font Family & Size Group -->
          <FontFamilyDropdown
            :model-value="currentFontFamily"
            @change="setFontFamily"
            class="margin-right-0"
          />
          <FontSizeDropdown
            :model-value="currentFontSize"
            @change="setFontSize"
            class="margin-right-0"
          />
        </ToolbarGroup>

        <!-- Text Formatting Group -->
        <ToolbarGroup>
          <ToolbarButton
            @click="executeCommand('toggleBold')"
            :active="isActive('bold')"
            title="Bold (Ctrl+B)"
          >
            <strong>B</strong>
          </ToolbarButton>
          <ToolbarButton
            @click="executeCommand('toggleItalic')"
            :active="isActive('italic')"
            title="Italic (Ctrl+I)"
          >
            <em>I</em>
          </ToolbarButton>
          <ToolbarButton
            @click="executeCommand('toggleUnderline')"
            :active="isActive('underline')"
            title="Underline (Ctrl+U)"
          >
            <u>U</u>
          </ToolbarButton>
          <ToolbarButton
            @click="executeCommand('toggleStrike')"
            :active="isActive('strike')"
            title="Strikethrough"
          >
            <s>S</s>
          </ToolbarButton>
          <ToolbarButton
            @click="executeCommand('setHorizontalRule')"
            title="Horizontal Rule"
          >
            ―
          </ToolbarButton>
        </ToolbarGroup>
         <!-- color Group -->
        <ToolbarGroup>
        <ColorPickerDropdown
            :model-value="currentTextColor"
            @change="setTextColor"
            @remove="removeTextColor"
            title="Text Color"
          />
          <HighlightPickerDropdown
            :model-value="currentHighlightColor"
            @change="setHighlight"
            @remove="removeHighlight"
            title="Highlight Color"
          />
        </ToolbarGroup>
        <!-- Alignment Group -->
        <ToolbarGroup>
          <AlignmentDropdown
            :model-value="currentAlignment"
            @change="setAlignment"
          />
          <LineHeightDropdown
            :model-value="currentLineHeight"
            @change="setLineHeight"
          />
          <ListDropdown
            :model-value="currentList"
            @change="setList"
          />
          <TableDropdown @insert="handleTableInsert" />
        </ToolbarGroup>

        <!-- Insert Group -->
        <ToolbarGroup>
          <ToolbarButton @click="$emit('insert-link')" title="Insert Link">
            <Link/>
          </ToolbarButton>
          <ToolbarButton @click="$emit('insert-image')" title="Insert Image">
            <ImageSvg/>
          </ToolbarButton>
          <ToolbarButton @click="$emit('insert-video')" title="Insert Video">
            <Video/>
          </ToolbarButton>
        </ToolbarGroup>

        <!-- Advanced Group -->
        <ToolbarGroup>
          <ToolbarButton
            @click="executeCommand('toggleBlockquote')"
            :active="isActive('blockquote')"
            title="Quote"
          >
            <Quote/>
          </ToolbarButton>
          <ToolbarButton
            @click="executeCommand('toggleCodeBlock')"
            :active="isActive('codeBlock')"
            title="Code Block"
          >
            <CodeBlock/>
          </ToolbarButton>
          <ToolbarButton @click="$emit('clear-formatting')" title="Clear Formatting">
            <ClearFormatting/>
          </ToolbarButton>
          <ToolbarButton
            @click="$emit('toggle-source-code')"
            :active="sourceCodeMode"
            title="Source Code"
          >
            <Code/>
          </ToolbarButton>
        </ToolbarGroup>
      </div>
    </div>
  </div>
</template>

<script>
import ToolbarGroup from './ToolbarGroup.vue'
import ToolbarButton from './ToolbarButton.vue'
import HeadingDropdown from './HeadingDropdown.vue'
import FontFamilyDropdown from './FontFamilyDropdown.vue'
import FontSizeDropdown from './FontSizeDropdown.vue'
import LineHeightDropdown from './LineHeightDropdown.vue'
import AlignmentDropdown from './AlignmentDropdown.vue'
import ListDropdown from './ListDropdown.vue'
import TableDropdown from './TableDropdown.vue'
import ColorPickerDropdown from './ColorPickerDropdown.vue'
import HighlightPickerDropdown from './HighlightPickerDropdown.vue'
import {
  Link,
  ImageSvg,
  Video,
  Previous,
  Next,
  Code,
  Quote,
  ClearFormatting,
  CodeBlock
} from '../svg'

export default {
  name: 'ToolbarMain',
  components: {
    ToolbarGroup,
    ToolbarButton,
    HeadingDropdown,
    FontFamilyDropdown,
    FontSizeDropdown,
    LineHeightDropdown,
    AlignmentDropdown,
    ListDropdown,
    TableDropdown,
    ColorPickerDropdown,
    HighlightPickerDropdown,
    Link,
    ImageSvg,
    Video,
    Previous,
    Next,
    Code,
    Quote,
    ClearFormatting,
    CodeBlock
  },
  props: {
    editor: {
      type: Object,
      default: null
    },
    sourceCodeMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      editorUpdateKey: 0
    }
  },
  emits: [
    'insert-link',
    'insert-image', 
    'insert-video',
    'clear-formatting',
    'toggle-source-code'
  ],
  computed: {
    canUndo() {
      // Force reactivity
      this.editorUpdateKey
      if (!this.editor) return false
      return this.editor.can().undo()
    },
    canRedo() {
      // Force reactivity
      this.editorUpdateKey
      if (!this.editor) return false
      return this.editor.can().redo()
    },
    currentFontFamily() {
      // Force reactivity
      this.editorUpdateKey
      if (!this.editor) return ''
      const { fontFamily } = this.editor.getAttributes('textStyle')
      return fontFamily || ''
    },
    currentFontSize() {
      // Force reactivity
      this.editorUpdateKey
      if (!this.editor) return ''
      
      // First check if there's an explicit fontSize set
      const { fontSize } = this.editor.getAttributes('textStyle')
      if (fontSize) return fontSize
      
      // Check for heading levels and return their default sizes
      if (this.editor.isActive('heading', { level: 1 })) return '32px' // 2em
      if (this.editor.isActive('heading', { level: 2 })) return '24px' // 1.5em
      if (this.editor.isActive('heading', { level: 3 })) return '20px' // 1.25em
      if (this.editor.isActive('heading', { level: 4 })) return '18px' // 1.1em
      if (this.editor.isActive('heading', { level: 5 })) return '16px' // 1em
      if (this.editor.isActive('heading', { level: 6 })) return '14px' // 0.9em
      
      return ''
    },
    currentHeading() {
      // Force reactivity
      this.editorUpdateKey
      if (!this.editor) return ''
      
      // Check for heading levels
      for (let level = 1; level <= 6; level++) {
        if (this.editor.isActive('heading', { level })) {
          return String(level)
        }
      }
      
      // Check if it's a paragraph
      if (this.editor.isActive('paragraph')) {
        return 'paragraph'
      }
      
      // Default to empty (shows "Select Style" placeholder)
      return ''
    },
    currentTextColor() {
      // Force reactivity
      this.editorUpdateKey
      if (!this.editor) return ''
      const { color } = this.editor.getAttributes('textStyle')
      return color || ''
    },
    currentHighlightColor() {
      // Force reactivity
      this.editorUpdateKey
      if (!this.editor) return ''
      const { color } = this.editor.getAttributes('highlight')
      return color || ''
    },
    currentLineHeight() {
      // Force reactivity
      this.editorUpdateKey
      if (!this.editor) return ''
      
      // Check paragraph first
      const paragraphAttrs = this.editor.getAttributes('paragraph')
      if (paragraphAttrs.lineHeight) return paragraphAttrs.lineHeight
      
      // Check heading
      for (let level = 1; level <= 6; level++) {
        if (this.editor.isActive('heading', { level })) {
          const headingAttrs = this.editor.getAttributes('heading')
          return headingAttrs.lineHeight || ''
        }
      }
      
      return ''
    },
    currentAlignment() {
      // Force reactivity
      this.editorUpdateKey
      if (!this.editor) return 'left'
      
      // Check for each alignment type
      if (this.editor.isActive({ textAlign: 'center' })) return 'center'
      if (this.editor.isActive({ textAlign: 'right' })) return 'right'
      if (this.editor.isActive({ textAlign: 'justify' })) return 'justify'
      
      return 'left'
    },
    currentList() {
      // Force reactivity
      this.editorUpdateKey
      if (!this.editor) return ''
      
      // Check for each list type
      if (this.editor.isActive('bulletList')) return 'bulletList'
      if (this.editor.isActive('orderedList')) return 'orderedList'
      if (this.editor.isActive('taskList')) return 'taskList'
      
      return ''
    }
  },
  watch: {
    editor: {
      immediate: true,
      handler(newEditor, oldEditor) {
        if (oldEditor) {
          oldEditor.off('selectionUpdate', this.handleEditorUpdate)
          oldEditor.off('transaction', this.handleEditorUpdate)
        }
        if (newEditor) {
          newEditor.on('selectionUpdate', this.handleEditorUpdate)
          newEditor.on('transaction', this.handleEditorUpdate)
        }
      }
    }
  },
  beforeDestroy() {
    if (this.editor) {
      this.editor.off('selectionUpdate', this.handleEditorUpdate)
      this.editor.off('transaction', this.handleEditorUpdate)
    }
  },
  methods: {
    handleEditorUpdate() {
      this.editorUpdateKey++
    },
    executeCommand(command, options = {}) {
      if (!this.editor) return
      this.$emit('execute-command', command, options)
    },
    
    isActive(name, attributes = {}) {
      if (!this.editor) return false
      return this.editor.isActive(name, attributes)
    },
    
    setHeading(event) {
      if (!this.editor) return
      const value = event.target.value
      
      if (value === 'paragraph') {
        this.executeCommand('setParagraph')
      } else {
        const level = parseInt(value)
        if (level) {
          this.executeCommand('toggleHeading', { level })
        }
      }
      // Don't reset select - let computed property handle it
    },
    
    setFontFamily(event) {
      if (!this.editor) return
      const fontFamily = event.target.value
      if (fontFamily) {
        this.executeCommand('setFontFamily', fontFamily)
      } else {
        this.editor.chain().focus().unsetFontFamily().run()
      }
      // Don't reset select - let computed property handle it
    },
    
    setFontSize(event) {
      if (!this.editor) return
      const fontSize = event.target.value
      if (fontSize) {
        this.executeCommand('setFontSize', fontSize)
      } else {
        this.editor.chain().focus().unsetFontSize().run()
      }
      // Don't reset select - let computed property handle it
    },
    
    setTextColor(color) {
      if (!this.editor) return
      this.executeCommand('setColor', color)
    },
    
    removeTextColor() {
      if (!this.editor) return
      this.editor.chain().focus().unsetColor().run()
    },
    
    setHighlight(color) {
      if (!this.editor) return
      this.executeCommand('setHighlight', { color })
    },
    
    removeHighlight() {
      if (!this.editor) return
      this.editor.chain().focus().unsetHighlight().run()
    },
    
    setLineHeight(event) {
      if (!this.editor) return
      const lineHeight = event.target.value
      if (lineHeight) {
        this.executeCommand('setLineHeight', lineHeight)
      } else {
        this.editor.chain().focus().unsetLineHeight().run()
      }
    },
    
    setAlignment(event) {
      if (!this.editor) return
      const alignment = event.target.value
      if (alignment) {
        this.executeCommand('setTextAlign', alignment)
      }
    },
    
    setList(event) {
      if (!this.editor) return
      const listType = event.target.value
      
      if (listType === 'bulletList') {
        this.executeCommand('toggleBulletList')
      } else if (listType === 'orderedList') {
        this.executeCommand('toggleOrderedList')
      } else if (listType === 'taskList') {
        this.executeCommand('toggleTaskList')
      }
    },
    
    handleTableInsert({ rows, cols }) {
      if (!this.editor) return
      this.editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run()
    }
  }
}
</script>